import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import { forwardRef } from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { Accordion } from '@hotjar/getting-started-guide';
import { FloatingPanel } from '@hotjar/activation-toolkit';

const Banner = styled.div`
  margin: 0 ${tokens.spacer.x1};
  height: 120px;
  background-position: center;
  background-size: cover;
  transition: background-image 500ms ease;
`;

const COLLAPSED_KEY = 'getStartedCollapsed';

export interface WizardProps {
  title: string;
  banner?: string;
  sectionToShow?: number;
  remainingTasks: number;
  children: React.ReactNode;
  onDismiss?: () => void;
  onToggle?: (isCollapsed: boolean) => void;
  onToggleSection?: (index: number) => void;
}

export interface CompoundedWizard
  extends ForwardRefExoticComponent<WizardProps & RefAttributes<HTMLDivElement>> {
  Section: typeof Accordion.Section;
}

export const Wizard = forwardRef<HTMLElement, WizardProps>(
  (
    {
      title,
      banner,
      sectionToShow,
      remainingTasks,
      onDismiss,
      onToggle,
      onToggleSection,
      children,
    },
    ref,
  ): JSX.Element => (
    <FloatingPanel
      collapsedStateKey={COLLAPSED_KEY}
      title={title}
      remainingTasks={remainingTasks}
      onDismiss={onDismiss}
      onToggle={onToggle}
      ref={ref}
    >
      {banner && (
        <Banner
          aria-hidden
          data-testid="get-started-section-banner"
          style={{ backgroundImage: `url('${banner}')` }}
        />
      )}
      <Accordion sectionToShow={sectionToShow} onChange={onToggleSection} setUncontrolled>
        {children}
      </Accordion>
    </FloatingPanel>
  ),
) as CompoundedWizard;

Wizard.Section = Accordion.Section;
